/**
 * Landing v3 — "the page has settled": the window has loaded, then the main
 * thread has a free moment (or `timeout` ms have passed, whichever is first).
 * Work the first view does not need — Ka's Rive runtime and its wasm, the
 * demo cards' chunks a phone fetches ahead — waits for it, so it never
 * competes with the hero for the network or the CPU while the page is still
 * becoming interactive.
 */
import { useEffect, useState } from 'react';

const settled = new Map<number, Promise<void>>();

export function whenLandingSettled(timeout = 2500): Promise<void> {
  let promise = settled.get(timeout);
  if (!promise) {
    promise = new Promise<void>((resolve) => {
      const idle = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => resolve(), { timeout });
        } else {
          window.setTimeout(resolve, Math.min(timeout, 1500));
        }
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    });
    settled.set(timeout, promise);
  }
  return promise;
}

/**
 * The first free moment, WITHOUT waiting for the window's load (Ka is in the
 * phone's first view; a slow image must not hold him back): an idle slot, or
 * `timeout` ms. False until then.
 */
export function useIdleSoon(timeout = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setReady(true), { timeout });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(() => setReady(true), 600);
    return () => window.clearTimeout(id);
  }, [timeout]);
  return ready;
}
